/*
 * Carousel Accessibility Enhancements
 * ------------------------------------
 * Applies WAI-ARIA Carousel (APG) best practices to every carousel/slider
 * instance in the theme (Swiper web-component sliders and Flickity sliders):
 *
 *   1. Only the currently visible slide(s) and their interactive content
 *      are part of the tab order.
 *   2. Hidden/inactive slides get tabindex="-1" (slide + all focusable
 *      descendants) and aria-hidden="true" so assistive tech skips them.
 *   3. Focusability/aria-hidden is restored automatically when a slide
 *      becomes active again.
 *   4. Every slide gets a unique accessible name:
 *      role="group" aria-roledescription="slide"
 *      aria-label="Slide X of Y - <slide title>"
 *   5. Previous/Next/pagination controls are left untouched so they keep
 *      working exactly as before (Swiper's own a11y module + Flickity's
 *      native buttons already make these keyboard accessible).
 *
 * This script does not modify Swiper/Flickity internals, autoplay,
 * navigation, analytics or markup/styling — it only layers ARIA/tabindex
 * bookkeeping on top of the slide elements that are already rendered.
 */
(function () {
  'use strict';

  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]',
    'video[controls]',
    'audio[controls]',
    'iframe',
    '[contenteditable="true"]',
    'summary'
  ].join(',');

  function getFocusableDescendants(el) {
    return Array.prototype.slice.call(el.querySelectorAll(FOCUSABLE_SELECTOR));
  }

  function computeSlideTitle(slideEl, container) {
    if (slideEl.hasAttribute('data-slide-title')) {
      return slideEl.getAttribute('data-slide-title').trim();
    }
    var heading = slideEl.querySelector('h1, h2, h3, h4, h5, h6, [data-slide-heading]');
    if (heading && heading.textContent && heading.textContent.trim()) {
      return heading.textContent.trim();
    }
    var img = slideEl.querySelector('img[alt]:not([alt=""])');
    if (img) {
      return img.getAttribute('alt').trim();
    }
    if (container) {
      var containerLabel = container.getAttribute('data-carousel-label') || container.getAttribute('aria-label');
      if (containerLabel && containerLabel.trim()) {
        return containerLabel.trim();
      }
    }
    return '';
  }

  // Set/restore tabindex + aria-hidden on a slide and its focusable content.
  function setSlideActive(slideEl, isActive) {
    var isCurrentlyInert = slideEl.getAttribute('data-a11y-inert') === 'true';

    if (isActive) {
      if (!isCurrentlyInert) return;
      slideEl.removeAttribute('aria-hidden');
      slideEl.removeAttribute('tabindex');
      slideEl.removeAttribute('data-a11y-inert');
      getFocusableDescendants(slideEl).forEach(function (el) {
        if (!el.hasAttribute('data-a11y-orig-tabindex')) return;
        var orig = el.getAttribute('data-a11y-orig-tabindex');
        if (orig === '') {
          el.removeAttribute('tabindex');
        } else {
          el.setAttribute('tabindex', orig);
        }
        el.removeAttribute('data-a11y-orig-tabindex');
      });
    } else {
      if (isCurrentlyInert) return;
      // Avoid leaving focus trapped inside an aria-hidden subtree.
      if (document.activeElement && slideEl.contains(document.activeElement) && document.activeElement !== document.body) {
        document.activeElement.blur();
      }
      slideEl.setAttribute('aria-hidden', 'true');
      slideEl.setAttribute('tabindex', '-1');
      slideEl.setAttribute('data-a11y-inert', 'true');
      getFocusableDescendants(slideEl).forEach(function (el) {
        if (!el.hasAttribute('data-a11y-orig-tabindex')) {
          el.setAttribute('data-a11y-orig-tabindex', el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : '');
        }
        el.setAttribute('tabindex', '-1');
      });
    }
  }

  function labelSlide(slideEl, index, total, container) {
    slideEl.setAttribute('role', 'group');
    slideEl.setAttribute('aria-roledescription', 'slide');
    if (!slideEl.hasAttribute('data-a11y-title-set')) {
      slideEl.setAttribute('data-a11y-title-set', 'true');
      var title = computeSlideTitle(slideEl, container);
      if (title) slideEl.setAttribute('data-a11y-title', title);
    }
    var storedTitle = slideEl.getAttribute('data-a11y-title') || '';
    slideEl.setAttribute('aria-label', 'Slide ' + index + ' of ' + total + (storedTitle ? ' - ' + storedTitle : ''));
  }

  /* ----------------------------- Swiper ----------------------------- */

  function updateSwiperContainer(container) {
    var slides = Array.prototype.slice.call(container.children).filter(function (node) {
      return node.tagName === 'SWIPER-SLIDE';
    });
    if (!slides.length) return;

    var realSlides = slides.filter(function (slideEl) {
      return !slideEl.classList.contains('swiper-slide-duplicate');
    });
    var total = realSlides.length || slides.length;

    // Make sure Swiper is tracking visible slides so multi-slides-per-view
    // carousels keep every simultaneously visible slide focusable.
    var swiperInstance = container.swiper;
    if (swiperInstance && !swiperInstance.destroyed && !swiperInstance.params.watchSlidesProgress) {
      swiperInstance.params.watchSlidesProgress = true;
      if (typeof swiperInstance.updateSlidesClasses === 'function') {
        swiperInstance.updateSlidesClasses();
      }
    }

    slides.forEach(function (slideEl, position) {
      var dataIndexAttr = slideEl.getAttribute('data-swiper-slide-index');
      var humanIndex = dataIndexAttr !== null ? (parseInt(dataIndexAttr, 10) % total) + 1 : position + 1;
      labelSlide(slideEl, humanIndex, total, container);

      var isVisible = slideEl.classList.contains('swiper-slide-active') || slideEl.classList.contains('swiper-slide-visible');
      setSlideActive(slideEl, isVisible);
    });
  }

  function scanSwiperContainers(root) {
    var scope = root || document;
    var containers = scope.matches && scope.matches('swiper-container') ? [scope] : Array.prototype.slice.call(scope.querySelectorAll('swiper-container'));
    containers.forEach(function (container) {
      updateSwiperContainer(container);
      // Swiper applies its slide classes synchronously, but give lazily
      // initialized sliders (init="false") a moment after they start up.
      setTimeout(function () { updateSwiperContainer(container); }, 200);
    });
  }

  var SWIPER_EVENTS = [
    'swiperinit',
    'swiperafterinit',
    'swiperslidechange',
    'swiperslidechangetransitionend',
    'swiperresize',
    'swiperbreakpoint',
    'swiperobserverupdate',
    'swiperautoplay',
    'swiperreachbeginning',
    'swiperreachend',
    'swiperfromedge',
    'swipertoedge'
  ];

  SWIPER_EVENTS.forEach(function (eventName) {
    document.addEventListener(eventName, function (event) {
      if (event.target && event.target.tagName === 'SWIPER-CONTAINER') {
        updateSwiperContainer(event.target);
      }
    });
  });

  /* ---------------------------- Flickity ---------------------------- */

  function getFlickitySlides(container) {
    var slider = container.querySelector(':scope > .flickity-viewport > .flickity-slider');
    if (!slider) return [];
    return Array.prototype.slice.call(slider.children);
  }

  function updateFlickityContainer(container) {
    var slides = getFlickitySlides(container);
    if (!slides.length) return;
    var total = slides.length;
    slides.forEach(function (slideEl, index) {
      labelSlide(slideEl, index + 1, total, container);
      var isVisible = slideEl.classList.contains('is-selected');
      setSlideActive(slideEl, isVisible);
    });
  }

  function observeFlickityContainer(container) {
    var slider = container.querySelector(':scope > .flickity-viewport > .flickity-slider');
    if (!slider || slider.getAttribute('data-a11y-observed') === 'true') return;
    slider.setAttribute('data-a11y-observed', 'true');
    flickityObserver.observe(slider, { attributes: true, attributeFilter: ['class'], subtree: true });
  }

  var flickityObserver = new MutationObserver(function (mutations) {
    var containers = [];
    mutations.forEach(function (mutation) {
      var target = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;
      var container = target && target.closest ? target.closest('.flickity-enabled') : null;
      if (container && containers.indexOf(container) === -1) containers.push(container);
    });
    containers.forEach(updateFlickityContainer);
  });

  function scanFlickityContainers(root) {
    var scope = root || document;
    var containers = scope.matches && scope.matches('.flickity-enabled') ? [scope] : Array.prototype.slice.call(scope.querySelectorAll('.flickity-enabled'));
    containers.forEach(function (container) {
      updateFlickityContainer(container);
      observeFlickityContainer(container);
    });
  }

  /* ------------------------- Shared bootstrap ------------------------- */

  function scanAll(root) {
    scanSwiperContainers(root);
    scanFlickityContainers(root);
  }

  var rootObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (
          (node.matches && (node.matches('swiper-container') || node.matches('.flickity-enabled'))) ||
          (node.querySelector && node.querySelector('swiper-container, .flickity-enabled'))
        ) {
          scanAll(node);
        }
      });
    });
  });

  function init() {
    scanAll(document);
    rootObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', function () {
      scanSwiperContainers(document);
      scanFlickityContainers(document);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
